import { useCallback, useEffect, useMemo, useState } from 'react';
import { FabrikUser, CheckInResponse } from '../../../types';
import { searchCheckIns } from '../../../services/api/endpoints/checkins';

export interface EnrichedCheckIn {
  id: string;
  user_id: string;
  userName: string;
  userEmail?: string;
  space_id: string;
  timestamp: string; // ISO
  used_quota_type: string;
  notes_count?: number;
}

function formatTodayYMD(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const ymd = `${y}-${m}-${d}`;
  return { from: ymd, to: ymd };
}

export function useCheckedInToday(
  token: string | null,
  selectedSpaceId: string,
  allUsers: FabrikUser[],
  enabled: boolean = true
) {
  const [data, setData] = useState<EnrichedCheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usersById = useMemo(() => {
    const map = new Map<string, FabrikUser>();
    allUsers.forEach(u => map.set(u.id, u));
    return map;
  }, [allUsers]);

  const load = useCallback(async (bypassCache = false) => {
    if (!token || !enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const { from, to } = formatTodayYMD();
      const res = await searchCheckIns(token, {
        spaceId: selectedSpaceId || undefined,
        fromDate: from,
        toDate: to,
        limit: 100,
        orderBy: 'timestamp',
        orderDirection: 'desc',
        bypassCache,
      });

      const enriched: EnrichedCheckIn[] = (res.items as CheckInResponse[]).map(item => {
        const u = usersById.get(item.user_id);
        const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.display_name || u?.email || 'Unknown user';
        return {
          id: item.id,
          user_id: item.user_id,
          userName: name,
          userEmail: u?.email,
          space_id: item.space_id,
          timestamp: item.timestamp,
          used_quota_type: item.used_quota_type,
          notes_count: u?.notes_count,
        };
      });

      setData(enriched);
    } catch (e) {
      console.error('Failed to load today check-ins', e);
      setError(e instanceof Error ? e.message : 'Failed to load check-ins');
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedSpaceId, usersById, enabled]);

  useEffect(() => {
    if (!enabled) return;
    load(false);
  }, [load, enabled]);

  // Re-enrich names when user list updates (e.g., background prefetch completes)
  useEffect(() => {
    if (!data.length) return;
    let changed = false;
    const updated = data.map(item => {
      const u = usersById.get(item.user_id);
      const newName = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.display_name || u?.email || 'Unknown user';
      const newEmail = u?.email;
      const newNotesCount = u?.notes_count;
      if (newName !== item.userName || newEmail !== item.userEmail || newNotesCount !== item.notes_count) {
        changed = true;
        return { ...item, userName: newName, userEmail: newEmail, notes_count: newNotesCount };
      }
      return item;
    });
    if (changed) setData(updated);
  }, [usersById, data]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, isLoading, error, refresh };
}

export default useCheckedInToday;
